import axios from "axios";
import { useOrderPaymentEligibility } from "@/api/use-order-payment-eligibility";
import { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { CreditCard, FileText } from "lucide-react";
import { PaymentMethodType, type ReturnTransportSummary } from "@/api.types.v2";
import { useOrderPayments } from "@/api/use-order-payments";
import { getReturnAmountDue } from "./return-refund-breakdown";
import {
  usePaymentMethods,
  useMyPaymentTerms,
} from "@/api/use-payment-methods-api";
import {
  NetPreparationRequiredError,
  useProcessPayment,
} from "@/api/use-process-payment";
import type { PaymentMethod } from "@/api.types";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const CARD_LOGO_BASE_URL =
  "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";

const CARD_BRAND_LOGOS: Record<string, string> = {
  mastercard: "mastercard.svg",
  visa: "visa.svg",
  amex: "american-express.svg",
  "american express": "american-express.svg",
  diners: "diners.svg",
  "diners club": "diners.svg",
  discover: "discover.svg",
  jcb: "jcb.svg",
  unionpay: "unionpay.svg",
};

function getCardBrandLogo(brand?: string): string {
  const logo = brand ? CARD_BRAND_LOGOS[brand.toLowerCase()] : null;
  return `${CARD_LOGO_BASE_URL}/${logo ?? "card-generic.svg"}`;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

type PaymentChoice = "SAVED_CARD" | "INVOICE";

interface PayOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  totalCents: number;
  orderNumber?: string;
  allowInvoice?: boolean;
  returnTransport?: ReturnTransportSummary;
  purpose?: "ORDER" | "RETURN" | "ADDRESS_CHANGE";
  addressChangeShipmentId?: string;
  addressChangeRequestId?: string;
}

export function PayOrderDialog({
  open,
  onOpenChange,
  orderId,
  totalCents,
  orderNumber,
  allowInvoice = true,
  returnTransport,
  purpose = "ORDER",
  addressChangeShipmentId,
  addressChangeRequestId,
}: PayOrderDialogProps) {
  const { t } = useTranslation("app");
  const {
    data: billingData,
    isLoading: isLoadingPaymentMethods,
    isError: methodsError,
    refetch: refetchMethods,
  } = usePaymentMethods();
  const {
    data: paymentTermsData,
    isLoading: isLoadingPaymentTerms,
    isError: termsError,
    refetch: refetchTerms,
  } = useMyPaymentTerms();

  const eligibilityQuery = useOrderPaymentEligibility(
    orderId,
    open,
    addressChangeRequestId
  );
  const eligibility = eligibilityQuery.data;
  const returnPayments = useOrderPayments(
    purpose === "RETURN" && open ? orderId : undefined
  );
  const returnAmountChanged =
    purpose === "RETURN" &&
    (!returnTransport ||
      getReturnAmountDue(returnTransport, returnPayments.data ?? []) !==
        totalCents);
  const amountCents =
    purpose !== "ORDER"
      ? totalCents
      : eligibility?.eligible
        ? eligibility.amountCents
        : totalCents;
  const invoiceAvailable = allowInvoice && eligibility?.allowInvoice === true;
  const readError =
    eligibilityQuery.isError ||
    methodsError ||
    termsError ||
    (purpose === "RETURN" && returnPayments.isError);

  const defaultPaymentMethod = billingData?.paymentMethods?.find(
    (pm) => pm.isDefault
  );
  const paymentMethods = billingData?.paymentMethods ?? [];
  const hasPaymentMethods = paymentMethods.length > 0;

  const hasNetTermsAccess = paymentTermsData?.hasNetTermsAccess ?? false;
  const availableCredit = paymentTermsData?.availableCredit ?? 0;
  const paymentTerms = paymentTermsData?.paymentTerms ?? [];
  const hasUnpaidInvoices = paymentTermsData?.hasUnpaidInvoices ?? false;
  const unpaidInvoiceCount = paymentTermsData?.unpaidInvoiceCount ?? 0;

  const [chosenPaymentChoice, setPaymentChoice] =
    useState<PaymentChoice | null>(defaultPaymentMethod ? "SAVED_CARD" : null);
  const paymentChoice =
    chosenPaymentChoice ?? (defaultPaymentMethod ? "SAVED_CARD" : null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(defaultPaymentMethod?.id ?? null);
  const [selectedPaymentTermsId, setSelectedPaymentTermsId] = useState<
    string | null
  >(null);

  const effectivePaymentTermsId =
    selectedPaymentTermsId ?? (paymentTerms[0]?.id || null);

  const [preparationStatus, setPreparationStatus] = useState<
    "PENDING" | "FAILED" | null
  >(null);

  const processPayment = useProcessPayment({
    onSuccess: (data) => {
      if (
        data.preparationStatus === "PENDING" ||
        data.preparationStatus === "FAILED"
      ) {
        setPreparationStatus(data.preparationStatus);
        return;
      }
      toast({
        title: t("payOrder.successTitle"),
        description:
          paymentChoice === "INVOICE"
            ? t("payOrder.invoiceSuccess")
            : t("payOrder.cardSuccess"),
      });
      onOpenChange(false);
    },
    onError: (error) => {
      if (error instanceof NetPreparationRequiredError) {
        toast({
          variant: "destructive",
          title: t("payOrder.preparationNeedsSupportTitle"),
          description: t("payOrder.preparationNeedsSupportBody"),
        });
        onOpenChange(false);
        return;
      }
      const serverError = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data.error
        : undefined;
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        void eligibilityQuery.refetch();
      }
      toast({
        variant: "destructive",
        title: t("payOrder.failedTitle"),
        description:
          serverError || error.message || t("payOrder.failedDescription"),
      });
    },
  });

  const handlePay = () => {
    if (!canProceed) return;
    if (paymentChoice === "SAVED_CARD" && selectedPaymentMethodId) {
      processPayment.mutate({
        orderId,
        paymentMethodId: selectedPaymentMethodId,
        method: PaymentMethodType.SAVED_CARD,
        amountCents,
        shipmentIds:
          purpose === "ADDRESS_CHANGE" && addressChangeShipmentId
            ? [addressChangeShipmentId]
            : undefined,
        addressChangeRequestId:
          purpose === "ADDRESS_CHANGE" ? addressChangeRequestId : undefined,
        returnTransportId: purpose === "RETURN" ? returnTransport?.id : null,
      });
    } else if (paymentChoice === "INVOICE" && effectivePaymentTermsId) {
      processPayment.mutate({
        orderId,
        method: PaymentMethodType.INVOICE,
        paymentTermsId: effectivePaymentTermsId,
      });
    }
  };

  const handleSelectSavedCard = () => {
    setPaymentChoice("SAVED_CARD");
    if (!selectedPaymentMethodId && defaultPaymentMethod) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id);
    }
  };

  if (defaultPaymentMethod && !selectedPaymentMethodId) {
    setSelectedPaymentMethodId(defaultPaymentMethod.id);
  }

  const isLoading =
    isLoadingPaymentMethods ||
    isLoadingPaymentTerms ||
    eligibilityQuery.isPending ||
    eligibilityQuery.isFetching ||
    (purpose === "RETURN" &&
      (returnPayments.isPending || returnPayments.isFetching));
  const canProceed =
    !preparationStatus &&
    !isLoading &&
    !readError &&
    !returnAmountChanged &&
    eligibility?.eligible === true &&
    (paymentChoice === "SAVED_CARD"
      ? paymentMethods.some((method) => method.id === selectedPaymentMethodId)
      : paymentChoice === "INVOICE" &&
        invoiceAvailable &&
        hasNetTermsAccess &&
        !hasUnpaidInvoices &&
        availableCredit >= amountCents &&
        paymentTerms.some((term) => term.id === effectivePaymentTermsId));

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="lg"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex-col items-start gap-1">
              <span>
                {t("payOrder.title", {
                  orderNumber: orderNumber ? ` SHP-${orderNumber}` : "",
                })}
              </span>
              <span className="text-small text-default-500 font-normal">
                {t("payOrder.subtitle")}
              </span>
            </ModalHeader>
            <ModalBody>
              {preparationStatus ? (
                <div role="status" className="space-y-3">
                  <p>{t("payOrder.paymentReceived")}</p>
                  <p>
                    {t(
                      preparationStatus === "PENDING"
                        ? "payOrder.preparationPending"
                        : "payOrder.preparationFailed"
                    )}
                  </p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : readError ? (
                <div role="alert" className="space-y-3">
                  <p>{t("payOrder.loadError")}</p>
                  <Button
                    variant="flat"
                    onPress={() => {
                      void eligibilityQuery.refetch();
                      if (purpose === "RETURN") void returnPayments.refetch();
                      void refetchMethods();
                      void refetchTerms();
                    }}
                  >
                    {t("actions.retry")}
                  </Button>
                </div>
              ) : returnAmountChanged ? (
                <p role="alert">{t("payOrder.returnAmountChanged")}</p>
              ) : !eligibility?.eligible ? (
                <p role="alert">
                  {t(`payOrder.eligibilityReasons.${eligibility?.reason}`, {
                    defaultValue:
                      eligibility?.message ?? t("payOrder.unavailable"),
                  })}
                </p>
              ) : (
                <div className="space-y-4">
                  <Card shadow="none" className="bg-default-50">
                    <CardBody className="py-4">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-medium">
                          {t("payOrder.totalAmount")}
                        </span>
                        <span className="text-2xl font-bold">
                          {formatCurrency(amountCents)}
                        </span>
                      </div>
                    </CardBody>
                  </Card>

                  <RadioGroup
                    value={paymentChoice ?? ""}
                    onValueChange={(value) =>
                      setPaymentChoice(value as PaymentChoice)
                    }
                    classNames={{ wrapper: "gap-3" }}
                  >
                    {hasPaymentMethods && (
                      <PaymentOptionCard
                        selected={paymentChoice === "SAVED_CARD"}
                        onClick={handleSelectSavedCard}
                      >
                        <Radio value="SAVED_CARD" className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="h-5 w-5 text-primary" />
                            <span className="text-base font-medium">
                              {t("payOrder.payWithSavedCard")}
                            </span>
                            <Chip color="success" variant="flat" size="sm">
                              {t("createShipment.instant")}
                            </Chip>
                          </div>
                          <p className="text-sm text-default-500 mb-3">
                            {t("payOrder.cardImmediate")}
                          </p>

                          {paymentChoice === "SAVED_CARD" && (
                            <div className="space-y-3 mt-3 border-t border-default-200 pt-3">
                              {paymentMethods.map((method: PaymentMethod) => (
                                <SavedCardItem
                                  key={method.id}
                                  method={method}
                                  selected={
                                    selectedPaymentMethodId === method.id
                                  }
                                  onClick={() =>
                                    setSelectedPaymentMethodId(method.id)
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </PaymentOptionCard>
                    )}

                    {invoiceAvailable &&
                      hasNetTermsAccess &&
                      !hasUnpaidInvoices &&
                      paymentTerms.length > 0 && (
                        <PaymentOptionCard
                          selected={paymentChoice === "INVOICE"}
                          isDisabled={availableCredit < amountCents}
                          onClick={() => setPaymentChoice("INVOICE")}
                        >
                          <Radio
                            value="INVOICE"
                            className="mt-1"
                            isDisabled={availableCredit < amountCents}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-5 w-5 text-primary" />
                              <span className="text-base font-medium">
                                {t("payOrder.invoiceNetTerms")}
                              </span>
                              <Chip
                                color={
                                  availableCredit < amountCents
                                    ? "warning"
                                    : "primary"
                                }
                                variant="flat"
                                size="sm"
                              >
                                {t(
                                  availableCredit < amountCents
                                    ? "payOrder.insufficientCreditTitle"
                                    : "createShipment.available"
                                )}
                              </Chip>
                            </div>
                            <p className="text-sm text-default-500 mb-1">
                              {t("createShipment.payWithinDays", {
                                days: paymentTerms[0]?.paymentDueDays || 30,
                              })}
                            </p>
                            <p className="text-sm text-default-500">
                              {t("createShipment.availableCredit", {
                                amount: formatCurrency(availableCredit),
                              })}
                            </p>

                            {availableCredit < amountCents && (
                              <p
                                role="status"
                                className="text-sm text-warning-700"
                              >
                                {t("payOrder.insufficientCredit")}
                              </p>
                            )}

                            {paymentChoice === "INVOICE" &&
                              paymentTerms.length > 1 && (
                                <div className="mt-3 border-t border-default-200 pt-3">
                                  <Select
                                    label={t("createShipment.selectTerms")}
                                    variant="flat"
                                    size="sm"
                                    selectedKeys={
                                      effectivePaymentTermsId
                                        ? [effectivePaymentTermsId]
                                        : []
                                    }
                                    onSelectionChange={(keys) => {
                                      const next = Array.from(keys)[0] as
                                        | string
                                        | undefined;
                                      if (next) setSelectedPaymentTermsId(next);
                                    }}
                                  >
                                    {paymentTerms.map((term) => (
                                      <SelectItem key={term.id}>
                                        {term.name}
                                      </SelectItem>
                                    ))}
                                  </Select>
                                </div>
                              )}
                          </div>
                        </PaymentOptionCard>
                      )}

                    {invoiceAvailable &&
                      hasNetTermsAccess &&
                      hasUnpaidInvoices && (
                        <Card
                          shadow="none"
                          className="cursor-not-allowed border border-warning/20"
                        >
                          <CardBody className="p-4">
                            <div className="flex items-start gap-3">
                              <Radio
                                value="INVOICE"
                                isDisabled
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText className="h-5 w-5 text-warning" />
                                  <span className="text-base font-medium text-default-500">
                                    {t("payOrder.invoiceNetTerms")}
                                  </span>
                                  <Chip
                                    color="warning"
                                    variant="flat"
                                    size="sm"
                                  >
                                    {t("createShipment.paymentRequired")}
                                  </Chip>
                                </div>
                                <p className="text-sm text-default-700 mb-3">
                                  {t("payOrder.netTermsRequiredBody", {
                                    count: unpaidInvoiceCount,
                                  })}
                                </p>
                                <Button
                                  color="warning"
                                  variant="flat"
                                  size="sm"
                                  onPress={() => {
                                    window.open("/invoices", "_blank");
                                  }}
                                >
                                  {t("createShipment.payInvoice", {
                                    count: unpaidInvoiceCount,
                                  })}
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      )}

                    {!hasPaymentMethods &&
                      (!invoiceAvailable ||
                        !hasNetTermsAccess ||
                        !paymentTerms.length) && (
                        <Card
                          shadow="none"
                          className="border border-warning/20 bg-warning/10"
                        >
                          <CardBody className="py-4">
                            <p className="text-base text-foreground">
                              {t("payOrder.noMethods")}
                            </p>
                          </CardBody>
                        </Card>
                      )}
                  </RadioGroup>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={onClose}
                isDisabled={processPayment.isPending}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                color="primary"
                onPress={handlePay}
                isDisabled={!canProceed || processPayment.isPending}
                isLoading={processPayment.isPending}
              >
                {processPayment.isPending
                  ? t("actions.processing")
                  : paymentChoice === "INVOICE"
                    ? t("payOrder.useNetTerms")
                    : t("payOrder.payAmount", {
                        amount: formatCurrency(amountCents),
                      })}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

function PaymentOptionCard({
  selected,
  onClick,
  children,
  isDisabled = false,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  isDisabled?: boolean;
}) {
  return (
    <Card
      shadow="none"
      isPressable
      isDisabled={isDisabled}
      aria-disabled={isDisabled}
      className={cn(
        "transition-all w-full",
        selected
          ? "border-primary border-2 bg-primary/5"
          : "border border-default-200 hover:border-default-300"
      )}
      onPress={isDisabled ? undefined : onClick}
    >
      <CardBody className="p-4">
        <div className="flex items-start gap-3">{children}</div>
      </CardBody>
    </Card>
  );
}

function SavedCardItem({
  method,
  selected,
  onClick,
}: {
  method: PaymentMethod;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation("app");
  const brandDisplay = method.brand
    ? `${method.brand.charAt(0).toUpperCase()}${method.brand.slice(1).toLowerCase()}`
    : t("settings.cardAlt");

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
        selected
          ? "border-primary bg-primary/5"
          : "border-default-200 hover:border-default-300"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <img
        src={getCardBrandLogo(method.brand)}
        alt={method.brand ?? t("settings.cardAlt")}
        className="h-6 w-auto"
      />
      <div className="flex-1">
        <span className="text-sm font-medium">
          {brandDisplay} **** {method.last4}
        </span>
        {method.isDefault && (
          <Chip variant="flat" color="default" size="sm" className="ml-2">
            {t("settings.default")}
          </Chip>
        )}
      </div>
      {selected && (
        <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
          <div className="h-2 w-2 rounded-full bg-white" />
        </div>
      )}
    </div>
  );
}
